/**
 * PATH: packages/job-hub/src/jobs/client/resolveJobRoutes.ts
 * FILE_ID: JOBHUB-JOBS-CLIENT-JOBRT-001
 * PURPOSE: Route-table segment 3 (last before fallback) — quote DOCX routing,
 *          invoices/:id + materials/:id updates, job sub-resources (stages/
 *          materials/files/folders), then the bare /jobs/:id catch-all.
 * WHY: Moved VERBATIM from djangoJobHubClient.ts (S4 rank 12). Sub-resource
 *      matches MUST precede the bare /jobs/:id catch-all (route-order trap).
 */
import type { SegmentResolver } from './routeTypes'
import { ID } from './routeTypes'
import { reshapeFilesPayload } from './filesReshape'

export const resolveJobRoutes: SegmentResolver = ({ verb, match }) => {
  // --- quote DOCX: standard QuoteSheet adapter ------------------------
  let m = match(`/quotes/${ID}/generate-docx`)
  if (m && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: `/api/v1/quote-sheets/${m[1]}/generate-docx/`,
    }
  }

  // External report.Invoice rows do not carry complete source line content.
  // Native invoice DOCX uses the money-document route in resolveMoneyRoutes.
  m = match(`/invoices/${ID}/generate-docx`)
  if (m) {
    return { kind: 'reject', rejectReason: 'Document generation is not available on the standard platform yet.' }
  }

  // --- invoices/:id update -------------------------------------------
  m = match(`/invoices/${ID}`)
  if (m) {
    return { kind: 'http', method: 'patch', url: `/api/report/invoices/hub/${m[1]}/` }
  }

  // --- one-time action reversal (UUID path segment) -------------------
  m = match(`/jobs/actions/${ID}/undo`)
  if (m) {
    return {
      kind: 'http',
      method: 'post',
      url: `/api/jobs/actions/${m[1]}/undo/`,
    }
  }

  // --- materials/:id update + delete ---------------------------------
  m = match(`/materials/${ID}`)
  if (m) {
    return {
      kind: 'http',
      method: verb === 'put' ? 'patch' : verb,
      url: `/api/report/materials/${m[1]}/`,
    }
  }

  // --- job sub-resources (specific before bare /jobs/:id) ------------
  // Per-job team & access (universal-entry rectification, 2026-07-09):
  // the platform has carried the full RBAC surface since MR 4/5/8
  // (report/views/job_permissions.py) — these routes finally wire the
  // shared hub onto it. Endpoints live at /api/jobs/... (NOT /api/report).
  m = match(`/jobs/${ID}/permissions/${ID}`)
  if (m) return { kind: 'http', method: verb, url: `/api/jobs/${m[1]}/permissions/${m[2]}/` }

  m = match(`/jobs/${ID}/permissions`)
  if (m) return { kind: 'http', method: verb, url: `/api/jobs/${m[1]}/permissions/` }

  m = match(`/jobs/${ID}/team/${ID}`)
  if (m) return { kind: 'http', method: verb, url: `/api/jobs/${m[1]}/team/${m[2]}/` }

  m = match(`/jobs/${ID}/team`)
  if (m) return { kind: 'http', method: verb, url: `/api/jobs/${m[1]}/team/` }

  m = match(`/jobs/${ID}/access`)
  if (m) return { kind: 'http', method: 'get', url: `/api/jobs/${m[1]}/access/` }

  m = match(`/jobs/${ID}/stages`)
  if (m) return { kind: 'http', method: verb, url: `/api/report/job/${m[1]}/stages/` }

  m = match(`/jobs/${ID}/materials`)
  if (m) return { kind: 'http', method: verb, url: `/api/report/job/${m[1]}/materials/` }

  m = match(`/jobs/${ID}/registers`)
  if (m) {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/report/job/${m[1]}/registers/`,
    }
  }

  m = match(`/jobs/${ID}/client-packs`)
  if (m) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/report/job/${m[1]}/client-packs/`,
    }
  }

  m = match(`/jobs/${ID}/activity`)
  if (m) {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/report/job/${m[1]}/activity/`,
    }
  }

  m = match(`/client-packs/${ID}/download`)
  if (m) {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/report/client-packs/${m[1]}/download/`,
    }
  }

  m = match(`/jobs/${ID}/pnl`)
  if (m) {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/report/reporting/job-cost-centre-pl/${m[1]}/`,
    }
  }

  m = match(`/jobs/${ID}/files/remove`)
  if (m && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/report/jobfiles/remove/' }
  }

  m = match(`/jobs/${ID}/files/upload`)
  if (m) {
    return { kind: 'http', method: 'post', url: '/api/report/jobfiles/', params: { job_id: m[1] } }
  }

  m = match(`/jobs/${ID}/files`)
  if (m) {
    const jobId = Number(m[1])
    return {
      kind: 'http',
      method: 'get',
      url: '/api/report/jobfiles/',
      // ?include=qdrive opts into the Wave 2 union envelope: legacy
      // JobFile rows ∪ QDrive FileRecord(job=...) rows + persisted
      // folder_paths. The bare endpoint stays a flat array for legacy
      // consumers (fileThunks), so the opt-in must ride here.
      params: { job_id: m[1], include: 'qdrive' },
      reshape: (data) => reshapeFilesPayload(jobId, data),
    }
  }

  m = match(`/jobs/${ID}/folders`)
  if (m && verb === 'post') {
    // Real server-side folders (Wave 2): persists a VirtualFolder chain
    // under jobs/{id}/ — replaces the old synthetic fake-success no-op.
    return {
      kind: 'http',
      method: 'post',
      url: '/api/report/jobfiles/folders/',
      params: { job_id: m[1] },
    }
  }

  // Reused from: resolveJobRoutes.ts (/jobs/:id/team mapping)
  m = match(`/jobs/${ID}/qa-assign`)
  if (m) return { kind: 'http', method: verb, url: `/api/jobs/${m[1]}/qa-assign/` }

  // --- bare job detail read + update ---------------------------------
  m = match(`/jobs/${ID}`)
  if (m) {
    return { kind: 'http', method: verb === 'put' ? 'patch' : verb, url: `/api/report/job/${m[1]}/hub/` }
  }

  return null
}
